import React from 'react'
import { useFormContext } from 'react-hook-form'
import { required, SaveButton, SimpleForm, useNotify, useTranslate } from 'react-admin'
import { Box, Button, Dialog, DialogContent, DialogTitle, useTheme } from '@mui/material'
import styledFormInputs from '../../../ui/inputs/styledFormInputs'
import { passwordPolicy, passwordsMatch, PasswordPolicyHint } from '../../../ui/inputs/passwordPolicy'
import { changeAccountPassword } from '../../../rest/restClient'

const validateRequired = [required('Champs obligatoire')]
const validateNewPassword = [required('Champs obligatoire'), passwordPolicy]
const validateConfirmPassword = [required('Champs obligatoire'), passwordsMatch('password')]

// uaa POST /account/change_password errors -> i18n keys (HttpError from
// fetchJson: status + parsed body { message, description })
const errorMessageKey = (error) => {
    const code = error?.body?.message || ''
    if (error?.status === 400 && code === 'error.invalid_current_password') return 'resources.users.wrong_current_password'
    if (error?.status === 400 && code === 'error.validation') return 'resources.users.password_invalid'
    if (error?.status === 429) return 'resources.users.too_many_attempts'
    return 'resources.users.password_change_failed'
}

// Cancel + submit. Submits by hand (no react-admin save context in a
// standalone form) and closes the dialog on success. A plain Box, not the
// react-admin Toolbar, which on phones fixes itself to the screen bottom.
const ChangePasswordToolbar = ({ onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const form = useFormContext();

    const submit = async (values) => {
        try {
            await changeAccountPassword({ oldPassword: values.old_password, password: values.password })
            notify(translate('resources.users.password_changed'), { type: 'info' })
            onClose()
        } catch (error) {
            console.error('[account] change password failed', error?.status, error?.body?.message)
            notify(translate(errorMessageKey(error)), { type: 'warning' })
        }
    }

    const handleClick = (event) => {
        event.preventDefault()
        return form.handleSubmit(submit)(event)
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', padding: '8px 16px 16px 16px' }}>
            <Button onClick={onClose}>
                {translate('ra.action.cancel')}
            </Button>
            <SaveButton
                type="button"
                label={translate('resources.users.change_my_password')}
                icon={<></>}
                onClick={handleClick}
            />
        </Box>
    )
}

const ChangePasswordInputs = ({ inputsData }) => styledFormInputs(inputsData)

// Opened by the "Change my password" button of the account page.
const ChangePasswordDialog = ({ open, onClose }) => {
    const translate = useTranslate();
    const theme = useTheme();

    const inputsData = [
        { name: 'old_password', label: translate('resources.users.current_password'), validate: validateRequired, type: "password", toggleVisibility: true },
        { name: 'password', label: translate('resources.users.new_password'), validate: validateNewPassword, type: "password", toggleVisibility: true, hint: <PasswordPolicyHint key="password-policy" source="password" /> },
        { name: 'cpassword', label: translate('resources.users.cpassword'), validate: validateConfirmPassword, type: "password", toggleVisibility: true },
    ]

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            sx={{
                "& .MuiPaper-root": {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700, fontSize: "20px", textAlign: 'center' }}>
                {translate('resources.users.change_my_password')}
            </DialogTitle>
            <DialogContent sx={{ padding: "0px" }}>
                {/* the form is unmounted with the dialog, so it reopens empty */}
                {open && (
                    <SimpleForm
                        record={{}}
                        defaultValues={{ old_password: '', password: '', cpassword: '' }}
                        toolbar={<ChangePasswordToolbar onClose={onClose} />}
                        onSubmit={() => undefined}
                    >
                        <ChangePasswordInputs inputsData={inputsData} />
                    </SimpleForm>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default ChangePasswordDialog
